// ===================================================================
// PA GRANTS — Module Layout with simplified hierarchical navigation
// FEMA Public Assistance Grant Management
// ===================================================================

import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@mantine/core';
import {
  IconLayoutDashboard, IconClipboardList, IconBuildingCommunity, IconAlertTriangle,
  IconFileDescription, IconReportAnalytics, IconChartBar, IconMap,
  IconClock, IconReceipt, IconSettings,
} from '@tabler/icons-react';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import { useRoleStore } from '../../store/roleStore';
import type { NavItem } from '../../types/nav';
import ApplicantPortalLayout from './portal/ApplicantPortalLayout';
import ModuleNav from '../../components/layout/ModuleNav';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const APPLICANT_SLUGS = ['pa_applicant_mgr', 'pa_applicant_staff', 'pa_applicant_viewer'];

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard',    path: '/pagrants/pa',             icon: IconLayoutDashboard, exact: true },
  { label: 'Disasters',    path: '/pagrants/pa/disasters',   icon: IconAlertTriangle },
  { label: 'Applicants',   path: '/pagrants/pa/applicants',  icon: IconBuildingCommunity },
  { label: 'Projects',     path: '/pagrants/pa/projects',    icon: IconClipboardList },
  { label: 'Map',              path: '/pagrants/pa/map',                icon: IconMap },
  { label: 'Reimbursements',  path: '/pagrants/pa/reimbursements',     icon: IconReceipt },
  { label: 'Time Extensions', path: '/pagrants/pa/time-extensions',    icon: IconClock, minLevel: 'editor' },
  { label: 'FEMA Forms',      path: '/pagrants/pa/forms',              icon: IconFileDescription, minLevel: 'editor' },
  { label: 'Reports',      path: '/pagrants/pa/reports',     icon: IconReportAnalytics, minLevel: 'editor' },
  { label: 'Analytics',    path: '/pagrants/pa/analytics',   icon: IconChartBar, minLevel: 'editor' },
  { label: 'Settings',     path: '/pagrants/pa/settings/checklist', icon: IconSettings, minLevel: 'admin' },
];

const NAV_GROUPS: { label: string; paths: string[] }[] = [
  { label: '', paths: ['/pagrants/pa', '/pagrants/pa/disasters', '/pagrants/pa/applicants', '/pagrants/pa/projects', '/pagrants/pa/reimbursements', '/pagrants/pa/map'] },
  { label: 'Admin', paths: ['/pagrants/pa/time-extensions', '/pagrants/pa/forms', '/pagrants/pa/reports', '/pagrants/pa/analytics', '/pagrants/pa/settings/checklist'] },
];

export default function PAGrantsLayout() {
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const slug = effectiveRole?.roleSlug ?? '';
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const visibleItems = useFilteredNav('pagrants', NAV_ITEMS);

  // Applicant roles get the scoped portal layout
  if (APPLICANT_SLUGS.includes(slug)) {
    return <ApplicantPortalLayout />;
  }

  const groups = NAV_GROUPS.map(g => ({
    ...g,
    items: g.paths
      .map(p => visibleItems.find(vi => vi.path === p))
      .filter(Boolean) as typeof visibleItems,
  })).filter(g => g.items.length > 0);

  const programsButton = (
    <Button variant="subtle" size="xs" color="gray" onClick={() => navigate('/pagrants')} style={{ marginRight: 8 }}>
      &larr; Programs
    </Button>
  );

  const currentPage = NAV_ITEMS.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  return (
    <div style={{ padding: '24px' }}>
      <Breadcrumbs
        items={[
          { label: 'Hub', to: '/' },
          { label: 'Disaster Grants', to: '/pagrants' },
          { label: 'Public Assistance', to: '/pagrants/pa' },
          ...(currentPage && currentPage.path !== '/pagrants/pa' ? [{ label: currentPage.label }] : []),
        ]}
      />
      <ModuleNav items={visibleItems} groups={groups} accentColor="#1E40AF" extra={programsButton} />
      <Outlet />
    </div>
  );
}
